import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getAuthToken } from '../utils/auth';
import { ArrowLeft, Send, Loader2, MessageCircle, Building } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminSupportPage — the super admin's view of the dealer support chat.
 * Left column: list of dealer threads (sorted by most recent activity,
 * unread badge in red). Right column: selected thread's message list + a
 * reply composer. Polls every 5s for new messages and unread updates.
 */
export default function AdminSupportPage() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [dealers, setDealers] = useState([]);   // full contact directory
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const token = getAuthToken();
      const [threadsRes, dealersRes] = await Promise.all([
        axios.get(`${API}/admin/support/threads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/dealers`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setThreads(threadsRes.data?.threads || []);
      const dealerList = Array.isArray(dealersRes.data) ? dealersRes.data : (dealersRes.data?.dealers || []);
      // Keep only active + trial dealers — churned ones don't belong in support directory
      setDealers(dealerList.filter(d => ['active', 'trial', 'paid'].includes((d.subscription_status || '').toLowerCase())));
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  const loadThread = async (threadId) => {
    if (!threadId) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/support/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data?.messages || []);
      await axios.post(`${API}/admin/support/threads/${threadId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Reflect the mark-read locally so the unread pill vanishes without
      // waiting for the next poll.
      setThreads((prev) => prev.map(t => t.id === threadId ? { ...t, unread_for_admin: 0 } : t));
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadThreads();
    const iv = setInterval(loadThreads, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadThread(selectedId);
    const iv = setInterval(() => loadThread(selectedId), 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    // auto-scroll to bottom whenever messages arrive
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = reply.trim();
    if (!body || !selectedId) return;
    setSending(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/admin/support/threads/${selectedId}/messages`,
        { body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, res.data]);
      setReply('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // Open (or create) a thread for a dealer who hasn't messaged yet.
  const openDealerThread = async (dealer) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/admin/support/threads/for-dealer/${dealer.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadThreads();
      setSelectedId(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open thread');
    }
  };

  // Threads keyed by dealer_id so we can flag which dealers already have a chat.
  const threadByDealer = React.useMemo(() => {
    const m = {};
    threads.forEach(t => { m[t.dealer_id] = t; });
    return m;
  }, [threads]);

  const filteredDealers = dealers.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (d.business_name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q)
    );
  });

  const selected = threads.find(t => t.id === selectedId);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16" data-testid="admin-support-page">
      <div className="border-b bg-white dark:bg-gray-900 px-6 py-4 flex items-center gap-3 sticky top-16 z-30">
        <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeft size={20} />
        </button>
        <MessageCircle size={22} className="text-blue-600" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Advisor Support Chat</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-73px-64px)]">
        {/* Thread list + dealer directory */}
        <aside className="border-r bg-white dark:bg-gray-900 overflow-y-auto">
          <div className="p-3 border-b sticky top-0 bg-white dark:bg-gray-900 z-10">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dealers…"
              className="w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              data-testid="admin-support-dealer-search"
            />
          </div>

          {loading ? (
            <div className="p-6 text-center text-gray-400"><Loader2 className="animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Active threads first */}
              {threads.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[11px] uppercase font-semibold text-gray-400 bg-gray-50 dark:bg-gray-800">Active Conversations</div>
                  {threads.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedId(t.id)}
                      className={`w-full text-left px-4 py-3 border-b transition-colors ${
                        selectedId === t.id
                          ? 'bg-blue-50 dark:bg-blue-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      data-testid={`support-thread-${t.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <Building size={14} className="text-gray-400 shrink-0" />
                          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {t.business_name || t.dealer_email}
                          </span>
                        </div>
                        {t.unread_for_admin > 0 && (
                          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0">
                            {t.unread_for_admin}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {t.last_message?.body || 'No messages yet'}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : ''}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {/* Directory of active + trial dealers who haven't messaged yet */}
              {(() => {
                const contactsWithoutThread = filteredDealers.filter(d => !threadByDealer[d.id]);
                if (contactsWithoutThread.length === 0) return null;
                return (
                  <div>
                    <div className="px-4 py-2 text-[11px] uppercase font-semibold text-gray-400 bg-gray-50 dark:bg-gray-800">
                      Dealer Directory ({contactsWithoutThread.length})
                    </div>
                    {contactsWithoutThread.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => openDealerThread(d)}
                        className="w-full text-left px-4 py-3 border-b hover:bg-gray-50 dark:hover:bg-gray-800"
                        data-testid={`support-dealer-contact-${d.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <Building size={14} className="text-gray-400 shrink-0" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {d.business_name || d.email}
                            </span>
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                            d.subscription_status === 'active' || d.subscription_status === 'paid'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>
                            {d.subscription_status === 'trial' ? 'Trial' : 'Active'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{d.email}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Click to start a chat</p>
                      </button>
                    ))}
                  </div>
                );
              })()}

              {threads.length === 0 && filteredDealers.length === 0 && (
                <div className="p-6 text-center text-gray-400 text-sm">
                  No dealers found.
                </div>
              )}
            </>
          )}
        </aside>

        {/* Message pane */}
        <section className="flex flex-col bg-white dark:bg-gray-900">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              Pick a dealer thread on the left to start chatting.
            </div>
          ) : (
            <>
              <div className="border-b px-6 py-3 flex items-center gap-2">
                <Building size={16} className="text-gray-400" />
                <span className="font-semibold text-gray-900 dark:text-white">
                  {selected?.business_name || selected?.dealer_email}
                </span>
                <span className="text-xs text-gray-400 ml-2">{selected?.dealer_email}</span>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {messages.length === 0 && (
                  <p className="text-center text-gray-400 text-sm">No messages yet — say hello.</p>
                )}
                {messages.map((m) => {
                  const mine = m.sender === 'admin';
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                        mine
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white rounded-bl-sm'
                      }`} data-testid={`msg-${m.id}`}>
                        {m.body}
                        <div className={`text-[10px] mt-1 ${mine ? 'text-blue-100' : 'text-gray-400'}`}>
                          {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t px-6 py-4">
                <div className="flex items-end gap-2">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Type your reply — Enter to send, Shift+Enter for newline"
                    rows={2}
                    className="flex-1 rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white resize-none"
                    data-testid="admin-reply-input"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !reply.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="admin-reply-send"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
